import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { DefaultModal } from '../../../shared/components/DefaultModal'
import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { useLogout } from '../../../hooks/useLogout'

// Type definitions for prank flow engine
type PrankAction =
  | { type: 'next' }
  | { type: 'reset' }
  | { type: 'redirect'; url: string }
  | { type: 'countdown'; seconds: number }
  | { type: 'noop' }

interface PrankStep {
  id: string
  title?: string
  message: string
  yesText?: string
  noText?: string
  onYes: PrankAction
  onNo: PrankAction
  showCloseButton?: boolean
}

// Prank flow configuration - the single source of truth for all joke steps
const PRANK_FLOW: PrankStep[] = [
  {
    id: 'step-1',
    title: 'Are you sure?',
    message: 'This action is irreversible. Like your last deploy.',
    yesText: 'Yes',
    noText: 'No',
    onYes: { type: 'next' }, // Goes to step-2
    onNo: { type: 'next' }, // Goes to step-3 (chicken out message) - handled by resolveAction
    showCloseButton: true
  },
  {
    id: 'step-2',
    title: 'Are you sure sure ?',
    message: "Still confident? Deleting the BE will invalidate 14 Jira tickets and one developer's soul.",
    yesText: 'Yes',
    noText: 'No',
    onYes: { type: 'next' }, // Goes to step-4
    onNo: { type: 'reset' },
    showCloseButton: true
  },
  {
    id: 'step-3',
    message: 'I knew you would chiken out',
    yesText: 'OK',
    noText: 'OK',
    onYes: { type: 'reset' },
    onNo: { type: 'reset' },
    showCloseButton: true
  },
  {
    id: 'step-4',
    message: 'BE sure it gonna be bad if you continue',
    yesText: 'Yes',
    noText: 'No',
    onYes: { type: 'next' }, // Goes to step-5
    onNo: { type: 'reset' },
    showCloseButton: false
  },
  {
    id: 'step-5',
    title: 'Final question',
    message: 'This is not normal behavior. You may need professional help. Should we redirect you?',
    yesText: 'Yes, I need help',
    noText: 'No, delete anyway',
    onYes: {
      type: 'redirect',
      url: 'https://www.mentalhealth.com/emergency'
    },
    onNo: {
      type: 'countdown',
      seconds: 10
    },
    showCloseButton: false
  }
]

export const BOCleanupPage: React.FC = () => {
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number }>({ x: 50, y: 50 })
  const [stepIndex, setStepIndex] = useState<number | null>(null)
  const [countdown, setCountdown] = useState(10)
  const [isCountdown, setIsCountdown] = useState(false)
  const [showFinalButton, setShowFinalButton] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const randomMoveIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const panicLevel = useRef(0)
  const logout = useLogout()

  // Initialize button position randomly
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current
      const buttonSize = 120 // Button diameter
      const padding = 20 // Minimum padding from edges

      const maxX = container.clientWidth - buttonSize - padding
      const maxY = container.clientHeight - buttonSize - padding

      const randomX = Math.random() * (maxX - padding) + padding
      const randomY = Math.random() * (maxY - padding) + padding

      setButtonPosition({ x: randomX, y: randomY })
    }
  }, [])

  // Calculate distance between two points
  const calculateDistance = useCallback((x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
  }, [])

  // Generate random position within bounds
  const getRandomPosition = useCallback((): { x: number; y: number } => {
    if (!containerRef.current || !buttonRef.current) {
      return buttonPosition
    }

    const container = containerRef.current
    const buttonSize = 120 // Button diameter
    const padding = 20 // Minimum padding from edges

    const maxX = container.clientWidth - buttonSize - padding
    const maxY = container.clientHeight - buttonSize - padding

    const randomX = Math.random() * (maxX - padding) + padding
    const randomY = Math.random() * (maxY - padding) + padding

    return { x: randomX, y: randomY }
  }, [buttonPosition])

  // Handle mouse move for proximity detection - only during final button phase
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Button is static initially - only move during final button phase (after countdown)
      if (!buttonRef.current || !showFinalButton || stepIndex !== null || isCountdown) {
        return
      }

      // During final button phase, button moves when mouse is near
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const containerRect = containerRef.current?.getBoundingClientRect()

      if (!containerRect) {
        return
      }

      // Calculate button center in container coordinates
      const buttonCenterX = buttonRect.left - containerRect.left + buttonRect.width / 2
      const buttonCenterY = buttonRect.top - containerRect.top + buttonRect.height / 2

      // Mouse position in container coordinates
      const mouseX = e.clientX - containerRect.left
      const mouseY = e.clientY - containerRect.top

      // Calculate distance from mouse to button center
      const distance = calculateDistance(mouseX, mouseY, buttonCenterX, buttonCenterY)

      // If mouse is within 10px of button, move it immediately
      if (distance < 10 + buttonRect.width / 2) {
        const newPosition = getRandomPosition()
        setButtonPosition(newPosition)
      }
    },
    [calculateDistance, getRandomPosition, stepIndex, isCountdown, showFinalButton]
  )

  // Start prank flow
  const startPrank = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      e.nativeEvent.stopImmediatePropagation()

      // If we're in final button phase, clicking logs out
      if (showFinalButton) {
        logout()
          .then(() => {
            window.location.href = 'https://www.mentalhealth.com/emergency'
          })
          .catch(() => {
            window.location.href = 'https://www.mentalhealth.com/emergency'
          })
        return
      }

      // Otherwise, start the prank flow
      if (stepIndex === null) {
        setStepIndex(0)
      }
    },
    [stepIndex, showFinalButton, logout]
  )

  // Resolve action based on type
  const resolveAction = useCallback(
    (action: PrankAction, isNoButton: boolean = false) => {
      switch (action.type) {
        case 'next': {
          // Handle branching:
          // - step-1 (index 0) No button goes to step-3 (index 2 - chicken out message)
          // - step-1 (index 0) Yes button goes to step-2 (index 1)
          // - step-2 (index 1) Yes button goes to step-4 (index 3)
          // - step-4 (index 3) Yes button goes to step-5 (index 4)
          if (stepIndex === 0 && isNoButton) {
            setStepIndex(2) // Go directly to step-3 (chicken out message)
          } else if (stepIndex === 1 && !isNoButton) {
            setStepIndex(3) // step-2 Yes goes to step-4
          } else {
            const nextIndex = stepIndex !== null ? stepIndex + 1 : null
            if (nextIndex !== null && nextIndex < PRANK_FLOW.length) {
              setStepIndex(nextIndex)
            }
          }
          break
        }
        case 'reset':
          setStepIndex(null)
          panicLevel.current = 0 // Reset panic level on reset
          break
        case 'redirect':
          logout()
          window.location.href = action.url
          break
        case 'countdown': {
          setCountdown(action.seconds)
          setIsCountdown(true)
          setStepIndex(null) // Hide modal during countdown
          break
        }
        case 'noop':
          // Do nothing
          break
      }
    },
    [stepIndex, logout]
  )

  // Countdown effect
  useEffect(() => {
    if (isCountdown) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current)
            }
            setIsCountdown(false)

            // After countdown, show final button phase instead of immediately logging out
            setShowFinalButton(true)
            // Reset countdown for display
            setCountdown(10)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    }
  }, [isCountdown])

  // Random movement effect for final button phase - moves randomly every 2 seconds
  useEffect(() => {
    if (showFinalButton) {
      // Start moving button randomly every 2 seconds
      randomMoveIntervalRef.current = setInterval(() => {
        const newPosition = getRandomPosition()
        setButtonPosition(newPosition)
      }, 2000)
    }

    return () => {
      if (randomMoveIntervalRef.current) {
        clearInterval(randomMoveIntervalRef.current)
      }
    }
  }, [showFinalButton, getRandomPosition])

  // Get current step from flow
  const currentStep = stepIndex !== null ? PRANK_FLOW[stepIndex] : null

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        backgroundColor: INTERAX_COLORS.offBlack,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: 'default'
      }}
    >
      {/* Delete BE Button */}
      <Box
        ref={buttonRef}
        onClick={startPrank}
        onMouseDown={(e) => {
          // Prevent moving during click
          e.stopPropagation()
        }}
        sx={{
          position: 'absolute',
          left: `${buttonPosition.x}px`,
          top: `${buttonPosition.y}px`,
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          backgroundColor: '#DC2626', // More vibrant red
          background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'left 0.2s ease-out, top 0.2s ease-out, box-shadow 0.3s ease-out, transform 0.2s ease-out',
          boxShadow: `
            0 0 30px rgba(220, 38, 38, 0.6),
            0 0 60px rgba(220, 38, 38, 0.4),
            0 0 90px rgba(220, 38, 38, 0.2),
            0 8px 32px rgba(0, 0, 0, 0.5),
            inset 0 2px 10px rgba(255, 255, 255, 0.2),
            inset 0 -2px 10px rgba(0, 0, 0, 0.3)
          `,
          userSelect: 'none',
          zIndex: 1000,
          pointerEvents: (stepIndex !== null && !showFinalButton) || isCountdown ? 'none' : 'auto',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          '&:hover': {
            boxShadow: `
              0 0 40px rgba(220, 38, 38, 0.8),
              0 0 80px rgba(220, 38, 38, 0.6),
              0 0 120px rgba(220, 38, 38, 0.3),
              0 12px 40px rgba(0, 0, 0, 0.6),
              inset 0 2px 15px rgba(255, 255, 255, 0.3),
              inset 0 -2px 15px rgba(0, 0, 0, 0.4)
            `,
            transform: 'scale(1.05)',
            background: 'linear-gradient(135deg, #F87171 0%, #EF4444 50%, #DC2626 100%)'
          },
          '&:active': {
            transform: 'scale(0.98)',
            boxShadow: `
              0 0 20px rgba(220, 38, 38, 0.5),
              0 0 40px rgba(220, 38, 38, 0.3),
              0 4px 16px rgba(0, 0, 0, 0.4),
              inset 0 2px 8px rgba(0, 0, 0, 0.4)
            `
          }
        }}
      >
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.buttonNormal,
            color: INTERAX_COLORS.white,
            fontWeight: 700,
            textAlign: 'center',
            userSelect: 'none',
            pointerEvents: 'none'
          }}
        >
          Delete BE
        </Typography>
      </Box>

      {/* Final Button Phase Title */}
      {showFinalButton && (
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 999,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline2,
              color: INTERAX_COLORS.white,
              fontWeight: 600
            }}
          >
            okey one last time if you have the mental fortitude
          </Typography>
        </Box>
      )}

      {/* Generic Prank Flow Modal */}
      {currentStep && (
        <DefaultModal
          isOpen={true}
          onClose={() => resolveAction({ type: 'reset' })}
          maxWidth="500px"
          showCloseButton={currentStep.showCloseButton !== false}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            {currentStep.title && (
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline4,
                  color: INTERAX_COLORS.white
                }}
              >
                {currentStep.title}
              </Typography>
            )}

            <Typography
              sx={{
                ...(currentStep.title ? INTERAX_TYPOGRAPHY.body1 : INTERAX_TYPOGRAPHY.headline4),
                color: INTERAX_COLORS.white
              }}
            >
              {currentStep.message}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}
            >
              <Button variant="primary" onClick={() => resolveAction(currentStep.onYes, false)}>
                {currentStep.yesText ?? 'Yes'}
              </Button>
              <Button variant="secondary" onClick={() => resolveAction(currentStep.onNo, true)}>
                {currentStep.noText ?? 'No'}
              </Button>
            </Box>
          </Box>
        </DefaultModal>
      )}

      {/* Countdown Modal */}
      {isCountdown && (
        <DefaultModal isOpen={true} onClose={() => {}} maxWidth="500px" showCloseButton={false}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline1,
                color: INTERAX_COLORS.redError,
                fontSize: '96px',
                fontWeight: 700
              }}
            >
              {countdown}
            </Typography>
          </Box>
        </DefaultModal>
      )}
    </Box>
  )
}
